/**
 * Utility functions for persisting filter state to sessionStorage
 * This provides a fallback mechanism when returning from product detail pages
 */

const STORAGE_KEY = 'productFilters';

/**
 * Save filter state to sessionStorage
 * @param {Object} state - Filter state object
 * @param {string} state.search - Search query
 * @param {Array} state.categories - Selected category slugs
 * @param {Object} state.filters - Property filters object
 * @param {number} state.page - Current page number
 */
export function saveFiltersToStorage(state) {
    try {
        // Only save if there are actual filters applied
        const hasFilters =
            state.search ||
            (state.categories && state.categories.length > 0) ||
            (state.filters && Object.keys(state.filters).length > 0) ||
            (state.page && state.page > 1);

        if (hasFilters) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } else {
            // Clear storage if no filters
            sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch (e) {
        console.warn('Failed to save filters to sessionStorage:', e);
    }
}

/**
 * Load filter state from sessionStorage
 * @returns {Object|null} Saved filter state or null if not found
 */
export function loadFiltersFromStorage() {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return null;

        const parsed = JSON.parse(stored);

        // Validate structure
        if (typeof parsed !== 'object') return null;

        return {
            search: parsed.search || '',
            categories: parsed.categories || [],
            filters: parsed.filters || {},
            page: parsed.page || 1,
        };
    } catch (e) {
        console.warn('Failed to load filters from sessionStorage:', e);
        return null;
    }
}

/**
 * Clear filter state from sessionStorage
 */
export function clearFiltersFromStorage() {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        console.warn('Failed to clear filters from sessionStorage:', e);
    }
}

/**
 * Check if there are any URL parameters present
 * @returns {boolean} True if URL has query parameters
 */
export function hasUrlParams() {
    if (typeof window === 'undefined') return false;
    return window.location.search.length > 1; // More than just '?'
}
